import clsx from 'clsx'
import { html, nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { property, query } from 'lit/decorators.js'

import { CloseSource } from '@/dap-design-system'
import { DdsElement } from '@/internal/dds-hu-element'
import { SizedMixin } from '@/internal/mixin/sizedMixin'
import { translate } from '@/localization/localization'

import { ButtonVariant } from '../button/button.component'
import type { ButtonSize } from '../icon-button/icon-button.component'
import styles from './modal.scss?inline'

const ModalBaseWithSized = SizedMixin(DdsElement)

export default class ModalBaseElement extends ModalBaseWithSized {
  @query('.modal') dialog!: HTMLDialogElement
  @query('.modal__box') modalBox!: HTMLElement
  @property({ type: Boolean, reflect: true }) open?: boolean = false
  @property({ type: Boolean, reflect: true }) fullScreen?: boolean = false
  @property({ type: String, reflect: true }) closeOnEsc = 'true'
  @property({
    type: String,
    reflect: true,
  })
  closeOnOverlayClick = 'true'
  @property({ type: String, reflect: true }) closeButton = 'true'
  @property() title: string
  @property() description: string
  @property() okButtonLabel: string
  @property() cancelButtonLabel: string
  @property() closeButtonLabel: string
  @property() okButtonVariant: ButtonVariant = 'primary'
  @property() cancelButtonVariant: ButtonVariant = 'outline'
  @property({ type: Boolean }) okButtonDisabled = false
  @property({ type: Boolean }) cancelButtonDisabled = false
  @property({ type: Boolean }) cancelButtonDanger = false
  @property({ type: Boolean }) okButtonDanger = false
  @property({ type: Boolean }) hideOkButton = false
  @property({ type: Boolean }) hideCancelButton = false
  /** Override size for the default OK button; when empty uses the modal's effective size. */
  @property() okButtonSize = ''
  /** Override sizeMap for the default OK button; when empty uses the modal's sizeMap. */
  @property() okButtonSizeMap = ''
  /** Override size for the default Cancel button; when empty uses the modal's effective size. */
  @property() cancelButtonSize = ''
  /** Override sizeMap for the default Cancel button; when empty uses the modal's sizeMap. */
  @property() cancelButtonSizeMap = ''
  /** Override size for the close icon button; when empty uses the modal's effective size. */
  @property() closeButtonSize = 'md'
  /** Override sizeMap for the close icon button; when empty uses the modal's sizeMap. */
  @property() closeButtonSizeMap = ''
  @property({ type: String }) footer = 'true'
  @property({ type: String }) header = 'true'
  @property({ type: Boolean }) wideFooter = false
  @property({ type: Boolean }) bottomFooter = false
  @property({ type: Boolean }) useNativeFocus = false
  @property({ type: Boolean }) fixedHeader = false
  @property({ type: Boolean }) fixedFooter = false
  @property({ type: Boolean }) noModal = false
  private previouslyFocusedElement: Element | null = null
  private focusableElements: HTMLElement[] = []

  private canceled = false
  private closeEventCanceled = false

  static readonly styles = unsafeCSS(styles)

  connectedCallback(): void {
    super.connectedCallback()
    // Set aria-hidden to true by default
    if (!this.hasAttribute('aria-hidden')) {
      this.setAttribute('aria-hidden', 'true')
      this.setAttribute('tabindex', '-1')
    }
  }

  attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ): void {
    if (name === 'title') {
      this.header = value ? 'true' : 'false'
    }
    super.attributeChangedCallback(name, _old, value)
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)
    if (
      _changedProperties.has('open') &&
      _changedProperties.get('open') !== undefined
    ) {
      if (this.open) {
        this.handleOpen()
      } else {
        this.handleCloseAction()
      }
    }
  }

  private handleOpen(): void {
    const beforeOpenEvent = this.emit('dds-before-open', undefined, {
      cancelable: true,
    })

    if (beforeOpenEvent.defaultPrevented) {
      return
    }

    // Reset canceled flag when opening
    this.canceled = false
    this.closeEventCanceled = false
    this.scrollLock(true)
    this.previouslyFocusedElement = this.getActiveElement()
    if (this.noModal) {
      this.dialog.show()
    } else {
      this.dialog.showModal()
    }
    this.dialog.addEventListener(
      'transitionend',
      () => {
        const openedEvent = this.emit('dds-opened', undefined, {
          cancelable: true,
        })

        if (openedEvent.defaultPrevented) {
          return
        }

        this.focusableElements = this.getFocusableElements()
        if (this.focusableElements.length > 0) {
          this.focusableElements[0].focus()
        }
      },
      { once: true },
    )
    this.setAttribute('aria-hidden', 'false')
    this.setAttribute('tabindex', '0')
  }

  private handleCloseAction(): void {
    const beforeCloseEvent = this.emit('dds-before-close', undefined, {
      cancelable: true,
    })

    if (beforeCloseEvent.defaultPrevented) {
      return
    }

    this.dialog.close()
    // Reset canceled flag when closing
    this.canceled = false
    this.scrollLock(false)
    this.dialog.addEventListener(
      'transitionend',
      () => {
        const closedEvent = this.emit('dds-closed', undefined, {
          cancelable: true,
        })

        if (closedEvent.defaultPrevented) {
          return
        }

        if (this.previouslyFocusedElement instanceof HTMLElement) {
          this.previouslyFocusedElement.focus()
        }
      },
      { once: true },
    )
    this.setAttribute('aria-hidden', 'true')
    this.setAttribute('tabindex', '-1')
  }

  scrollLock = (open: boolean) => {
    if (open) {
      document.body.style.overflow = 'hidden'
      document.body.dataset.modalOpen = 'true'
    } else {
      document.body.style.overflow = ''
      delete document.body.dataset.modalOpen
    }
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    if (this.open) {
      this.setAttribute('aria-hidden', 'false')

      this.dialog.showModal()
    } else {
      this.setAttribute('aria-hidden', 'true')
    }
  }

  public show(): void {
    this.open = true
  }

  public hide(): void {
    this.open = false
  }

  private handleClose(source: CloseSource) {
    const handleCloseEvent = this.emit(
      'dds-close',
      {
        source,
      },
      {
        cancelable: true,
      },
    )

    if (handleCloseEvent.defaultPrevented) {
      this.closeEventCanceled = true
      return
    }

    this.closeEventCanceled = false
    this.hide()
  }

  private handleCancel(event: Event): void {
    // This method is called when the dialog's cancel event fires (Escape key)
    // We only want to handle it when closeOnEsc is false
    if (this.closeOnEsc !== 'true') {
      event.preventDefault()
      this.canceled = true
      return
    }

    // If the close event was canceled, prevent the native cancel behavior
    if (this.closeEventCanceled) {
      event.preventDefault()
      this.closeEventCanceled = false // Reset after handling
      return
    }

    // If closeOnEsc is true and event wasn't canceled, prevent default
    // to let the keydown handler deal with it
    event.preventDefault()
  }

  private handleEscClose(event: Event): void {
    // This method is called when the dialog's close event fires
    // The close event is not cancelable, so if the custom event was canceled,
    // we need to reopen the dialog

    // If the close event was canceled, reopen the dialog
    if (this.closeEventCanceled) {
      // Reopen the dialog since the close was canceled
      this.dialog.showModal()
      this.closeEventCanceled = false // Reset after handling
      return
    }

    // We only want to handle it when it's not from an escape key press
    // or when closeOnEsc is false
    if (this.canceled || this.closeOnEsc !== 'true') {
      event.preventDefault()
      return
    }

    // If it's from escape key and closeOnEsc is true, let the keydown handler deal with it
    event.preventDefault()
  }

  private handleOkButton(): void {
    const handleCloseEvent = this.emit(
      'dds-ok',
      {
        source: 'ok-button',
      },
      {
        cancelable: true,
      },
    )

    if (handleCloseEvent.defaultPrevented) {
      return
    }

    this.handleClose('ok-button')
  }

  private handleCancelButton(): void {
    const handleCloseEvent = this.emit(
      'dds-cancel',
      {
        source: 'cancel-button',
      },
      {
        cancelable: true,
      },
    )

    if (handleCloseEvent.defaultPrevented) {
      return
    }

    this.handleClose('cancel-button')
  }

  private handleCloseButton(): void {
    const handleCancelEvent = this.emit(
      'dds-cancel',
      {
        source: 'close-button',
      },
      {
        cancelable: true,
      },
    )

    if (handleCancelEvent.defaultPrevented) {
      return
    }

    this.handleClose('close-button')
  }

  private handleClick(event: MouseEvent): void {
    if (
      this.closeOnOverlayClick === 'true' &&
      !event.composedPath().includes(this.modalBox)
    ) {
      this.handleClose('overlay')
    }
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Tab' && !this.useNativeFocus) {
      this.handleFocusTrap(event)
      return
    }

    if (event.key === 'Escape') {
      if (this.closeOnEsc === 'true') {
        // Reset the flag before handling close
        this.closeEventCanceled = false

        // Emit dds-cancel event first
        const handleCancelEvent = this.emit(
          'dds-cancel',
          {
            source: 'esc',
          },
          {
            cancelable: true,
          },
        )

        if (handleCancelEvent.defaultPrevented) {
          event.preventDefault()
          event.stopImmediatePropagation()
          return
        }

        // Only handle escape if closeOnEsc is true
        this.handleClose('esc')
        // If the event was canceled, prevent the default ESC behavior
        if (this.closeEventCanceled) {
          event.preventDefault()
          event.stopImmediatePropagation()
        }
      } else {
        // Prevent default behavior when closeOnEsc is false
        event.preventDefault()
        event.stopImmediatePropagation()
      }
    }
  }

  private getFocusableElements(): HTMLElement[] {
    const focusableSelectors = [
      'button',
      '[href]',
      'input',
      'select',
      'textarea',
      '[tabindex]:not([tabindex="-1"])',
      '[role="button"]',
      '[role="link"]',
      '[role="menuitem"]',
      '[role="tab"]',
      '[role="option"]',
      '[role="radio"]',
      '[role="checkbox"]',
      '[role="switch"]',
      '[role="combobox"]',
      '[role="listbox"]',
      '[role="textbox"]',
      '[contenteditable="true"]',
      'dap-ds-input',
      'dap-ds-textarea',
      'dap-ds-select',
      'dap-ds-button',
      'dap-ds-icon-button',
      'dap-ds-search-input',
      'dap-ds-combobox',
      'dap-ds-checkbox',
      'dap-ds-radio',
      'dap-ds-switch',
      'dap-ds-slider',
      'dap-ds-file-input',
      'dap-ds-calendar',
      'dap-ds-date-picker',
      'slot',
    ]

    const getAllFocusableElements = (
      root: Element | ShadowRoot,
    ): HTMLElement[] => {
      const elements: HTMLElement[] = []

      // Get all elements matching the selectors in the current root
      const matches = root.querySelectorAll(focusableSelectors.join(','))
      matches.forEach(element => {
        if (element instanceof HTMLSlotElement) {
          const assignedElements = element.assignedElements()
          assignedElements.forEach(assignedElement => {
            if (assignedElement instanceof HTMLElement) {
              elements.push(assignedElement)
            }
          })
        } else if (element instanceof HTMLElement) {
          elements.push(element)
        }
      })

      return elements
    }

    return getAllFocusableElements(this.modalBox)
  }

  private getActiveElement(): Element | null {
    let activeElement: Element | null = document.activeElement
    if (!activeElement) return null

    // Traverse shadow DOM boundaries to find the actual focused element
    while (activeElement && activeElement.shadowRoot) {
      const shadowActive: Element | null =
        activeElement.shadowRoot.activeElement
      if (shadowActive) {
        activeElement = shadowActive
      } else {
        break
      }
    }

    return activeElement
  }

  private getElementFromElement(element: Element): HTMLElement {
    // If it's a shadow DOM component, try to get the actual focusable element
    if (element.shadowRoot) {
      // Check if the component has a focusElement getter (like dap-ds-button)
      const component = element as any
      if (
        component.focusElement &&
        component.focusElement instanceof HTMLElement
      ) {
        return component.focusElement
      }

      // Otherwise, try to find the focusable element inside the shadow root
      const focusableSelectors = [
        'button',
        '[href]',
        'input',
        'select',
        'textarea',
        '[tabindex]:not([tabindex="-1"])',
        '[role="button"]',
        '[role="link"]',
      ]
      const focusable = element.shadowRoot.querySelector(
        focusableSelectors.join(','),
      )
      if (focusable instanceof HTMLElement) {
        return focusable
      }
    }
    return element as HTMLElement
  }

  private isElementContainedIn(element: Element, container: Element): boolean {
    // Check if element is the same as container
    if (element === container) {
      return true
    }

    // Check if element is inside container's shadow root
    if (container.shadowRoot && container.shadowRoot.contains(element)) {
      return true
    }

    // Check if container is a shadow DOM component and element is its focusElement
    const component = container as any
    if (component.focusElement && component.focusElement === element) {
      return true
    }

    // Check if element is contained in the regular DOM tree
    return container.contains(element)
  }

  private handleFocusTrap(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || this.useNativeFocus) return

    if (this.focusableElements.length === 0) {
      this.focusableElements = this.getFocusableElements()
    }

    const firstFocusableElement = this.getElementFromElement(
      this.focusableElements[0],
    )
    const lastFocusableElementRaw =
      this.focusableElements[this.focusableElements.length - 1]
    const lastFocusableElement = this.getElementFromElement(
      lastFocusableElementRaw,
    )

    const activeElement = this.getActiveElement()

    if (!activeElement) return

    if (event.shiftKey) {
      // Check if active element is the first focusable element or contained within it
      if (
        activeElement === firstFocusableElement ||
        this.isElementContainedIn(activeElement, firstFocusableElement) ||
        this.isElementContainedIn(activeElement, this.focusableElements[0])
      ) {
        event.preventDefault()
        if (lastFocusableElement) {
          lastFocusableElement.focus()
        }
      }
    } else {
      // Check if active element is the last focusable element or contained within it
      if (
        activeElement === lastFocusableElement ||
        this.isElementContainedIn(activeElement, lastFocusableElement) ||
        this.isElementContainedIn(activeElement, lastFocusableElementRaw)
      ) {
        event.preventDefault()
        if (firstFocusableElement) {
          firstFocusableElement.focus()
        }
      }
    }
  }

  renderModal() {
    return html`<dialog
      part="base"
      class=${clsx('modal', {
        'modal--open': this.open,
        'modal--full': this.fullScreen,
        'modal--no-modal': this.noModal,
        [`modal--${this.size}`]: this.size,
      })}
      aria-label=${`${this.title ?? ''} ${this.description ?? ''}`}
      role="dialog"
      @close=${this.handleEscClose}
      @cancel=${this.handleCancel}
      @click=${this.handleClick}
      @keydown=${this.handleKeyDown}>
      <div part="panel" class="modal__box" aria-modal="true">
        <div
          class=${clsx('modal__header-container', {
            [`modal__header-container--${this.size}`]: this.size,
            [`modal__header-container--${this.size}-fixed`]: this.fixedHeader,
          })}
          part="header-container">
          ${this.header === 'true'
            ? html`<header class="modal__header" part="header" id="header">
                <slot name="title">
                  <dap-ds-typography variant="h3" part="title"
                    >${this.title}</dap-ds-typography
                  >
                </slot>
                <slot name="description">
                  <span
                    class="modal__description"
                    part="description"
                    id="description">
                    ${this.description}
                  </span>
                </slot>
              </header>`
            : html`<span></span>`}
          ${this.closeButton === 'true'
            ? html`<dap-ds-icon-button
                part="closebutton"
                class="modal__close"
                size=${(this.closeButtonSize ||
                  this.effectiveSize) as ButtonSize}
                sizeMap=${this.closeButtonSizeMap || this.sizeMap}
                aria-label=${this.closeButtonLabel
                  ? this.closeButtonLabel
                  : translate('close')}
                @click=${this.handleCloseButton}>
                <dap-ds-icon name="close-line"></dap-ds-icon>
              </dap-ds-icon-button>`
            : nothing}
        </div>
        <div
          class=${clsx('modal__content', {
            'modal__content--bottom': this.bottomFooter,
          })}
          part="content">
          <slot></slot>
        </div>
        ${this.footer === 'true'
          ? html`<footer
              class=${clsx('modal__footer', {
                'modal__footer--wide': this.wideFooter,
                [`modal__footer--${this.size}`]: this.size,
                [`modal__footer--${this.size}-fixed`]: this.fixedFooter,
              })}
              part="footer">
              <slot name="footer">
                ${this.hideOkButton
                  ? nothing
                  : html`<dap-ds-button
                      size=${(this.okButtonSize ||
                        this.effectiveSize) as ButtonSize}
                      sizeMap=${this.okButtonSizeMap || this.sizeMap}
                      @click=${this.handleOkButton}
                      variant=${this.okButtonVariant}
                      ?danger=${this.okButtonDanger}
                      ?disabled=${this.okButtonDisabled}
                      ?hidden=${this.hideOkButton}
                      >${this.okButtonLabel ?? translate('ok')}</dap-ds-button
                    >`}
                ${this.hideCancelButton
                  ? nothing
                  : html`<dap-ds-button
                      size=${(this.cancelButtonSize ||
                        this.effectiveSize) as ButtonSize}
                      sizeMap=${this.cancelButtonSizeMap || this.sizeMap}
                      variant=${this.cancelButtonVariant}
                      ?danger=${this.cancelButtonDanger}
                      ?disabled=${this.cancelButtonDisabled}
                      ?hidden=${this.hideCancelButton}
                      @click=${this.handleCancelButton}
                      >${this.cancelButtonLabel ??
                      translate('cancel')}</dap-ds-button
                    >`}
              </slot>
            </footer>`
          : nothing}
      </div>
    </dialog>`
  }
}
